import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Dialog, DialogContent, DialogTitle, InputLabel, TextField } from '@mui/material'
import { type FormEvent, useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { DomainError } from 'src/utils/error'
import { capture } from 'src/utils/sentry'
import { z } from 'zod'

type Props = {
  open: boolean
  onSubmit: (v: FormState) => Promise<void>
  onClose: () => void
}

export const schema = z.object({
  name: z.string().min(2, '2文字以上入力してください'),
  address: z.string(),
  iconURL: z.string(),
})
export type FormState = z.infer<typeof schema>
const defaultValues: FormState = {
  name: '',
  address: '',
  iconURL: '',
}

export const CreateClientDialog = ({ open, onSubmit, onClose }: Props) => {
  const { control, reset, handleSubmit, formState } = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'all',
  })

  const handleClose = useCallback(() => {
    onClose()
    reset()
  }, [onClose, reset])

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      try {
        await handleSubmit(onSubmit)()
      } catch (err) {
        capture(err)
        toast.error(err instanceof DomainError ? err.message : 'グループの作成に失敗しました')
      }
    },
    [onSubmit, handleSubmit],
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>グループ作成</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={submit}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <>
                <InputLabel>名前</InputLabel>
                <TextField required fullWidth type="text" {...field} error={!!formState.errors?.name} helperText={formState.errors?.name?.message} />
              </>
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <>
                <InputLabel>住所</InputLabel>
                <TextField fullWidth type="text" {...field} error={!!formState.errors?.address} helperText={formState.errors?.address?.message} />
              </>
            )}
          />
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button variant="outlined" onClick={handleClose}>
              キャンセル
            </Button>
            <Button variant="contained" type="submit" disabled={!formState.isValid || formState.isSubmitting}>
              作成
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
